// SCR-HOME упрощённый (E06-T04, ux/03 §1): шапка ресурсов из GET /me/wallet, сцена гаража без зон,
// быстрые входы: «В бой» (→ SCR-MAP), «Персонаж», «Ларёк». Открывается из кэша до ответа сервера;
// офлайн — данные с подписью времени. В обучении T02 — вид снаружи и замок (ГД 12 §3).
import { useEffect, useRef, useState } from 'preact/hooks';
import { useApp } from '../app/app.js';
import { ApiCallError } from '../net/api-client.js';
import { districts, player, saveCache, setWallet, tutorialActive, tutorialStep, wallet } from '../state/game.js';
import { stage } from '../ui/App.js';
import { ResourceHeader, useActionBar } from '../ui/kit.js';
import type { ScreenProps } from '../ui/App.js';
import { completeStep, setTut, startTutorial, TEXT } from '../tutorial/flow.js';
import { I } from '../assets/icons.js';
import { pickHomeBoss } from './map.js';
import { ZoneLayer } from './garage.js';
import { car, trips, tripState } from './car.js';
import { countClaimable } from './quests.js';
import { mailUnclaimed, questsClaimable } from '../state/quests.js';
import { onWs } from '../net/ws.js';
import { carStage } from '../content/garage.js';
import { useNow } from '../ui/kit.js';
import { manifestLoaded } from '../assets/manifest.js';
import { LAST_BATTLE_KEY, type LastBattle } from '../boot/boot.js';
import { storage } from '../state/storage.js';
import { t as tr } from '../i18n/t.js';
import { HomeRail } from './home-rail.js';

export const T02_HELP_MS = 8000;
export const GATE_OPEN_MS = 2000;

function LockStep() {
  const app = useApp();
  const [taps, setTaps] = useState(0);
  const [opening, setOpening] = useState(false);
  const done = useRef(false);
  const open = async (auto: boolean) => {
    if (done.current) return;
    done.current = true;
    setOpening(true);
    setTut({ target: null, say: auto ? TEXT.T02auto : TEXT.T02done, hint: null });
    app.haptic('success');
    const [r] = await Promise.all([completeStep(app, 'T02', auto ? 'auto' : 'unlock'), new Promise((res) => setTimeout(res, GATE_OPEN_MS))]);
    startTutorial(app, r.step);
  };
  useEffect(() => {
    const help = setTimeout(() => { void open(true); }, T02_HELP_MS); // в 8 с Семёныч крутит сам, ворота открыты к 10 с
    const more = setTimeout(() => { if (!done.current) setTut({ hint: TEXT.T02more }); }, 5000);
    return () => { clearTimeout(help); clearTimeout(more); };
  }, []);
  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
      <button
        data-tut="lock"
        data-testid="lock"
        aria-label={tr('CL-SCREENS-HOME-01')}
        style={{ pointerEvents: 'auto', width: 96, height: 112, fontSize: 72, transform: opening ? 'translateY(-40px) rotate(-30deg)' : `rotate(${taps * 12}deg)`, transition: 'transform .3s', opacity: opening ? 0 : 1 }}
        onClick={() => {
          if (done.current) return;
          app.haptic('light');
          const n = taps + 1;
          setTaps(n);
          if (n >= 3) void open(false);
        }}
      ><I n="lock" size={72} /></button>
    </div>
  );
}

/** Машина на сцене гаража: тап → SCR-CAR; флажок «Вернулась», если выезд закончен (E06-T07). */
function CarPin() {
  const app = useApp();
  const now = useNow(5000);
  useEffect(() => { app.call('getTrips').then((t) => { trips.value = t; }).catch(() => {}); }, []);
  const st = tripState(trips.value?.running ?? null, now);
  return (
    <button class="car-pin" data-testid="home-car" data-tut="home-car" aria-label={tr('UI-CAR-TITLE')} onClick={() => { app.haptic('light'); app.nav.push('SCR-CAR'); }}>
      {st === 'back' ? <span class="badge" data-testid="car-back">{tr('UI-HOME-CAR-BACK')}</span> : st === 'running' ? <span class="badge soft">{tr('CL-CAR-ON-ROAD')}</span> : null}
    </button>
  );
}

/** Счётчики главного (ux/03 §1): письма с невзятыми вложениями (бейдж Почты в колонке «Ещё»), задания; WS mail.new. */
function HomeCounts() {
  const app = useApp();
  useEffect(() => {
    app.call('getMail').then((r) => { mailUnclaimed.value = r.unclaimed; }).catch(() => {});
    app.call('getQuests').then((r) => { questsClaimable.value = countClaimable(r); }).catch(() => {});
    return onWs((ev) => { if (ev.t === 'mail.new') mailUnclaimed.value = ev.d.count; });
  }, []);
  return null;
}

export function HomeScreen(_p: ScreenProps) {
  const app = useApp();
  const step = tutorialStep.value;
  const w = wallet.value;
  const lowEnergy = !!w && w.energy.value < 10;
  // Манифест пришёл позже сцены — перерисовать гараж с фоном BG-GARAGE.
  // Вид Ласточки по узлам (Р-97); в выезде машины на сцене нет. Узлы — из кэша экрана машины или запросом.
  const away = tripState(trips.value?.running ?? null, Date.now()) === 'running';
  const carId = away ? null : `CAR-L${carStage(car.value?.nodes ?? [])}`;
  useEffect(() => { if (!car.value && step !== 'T02') app.call('getCar').then((c) => { car.value = c; }).catch(() => {}); }, []);
  useEffect(() => { void stage.value?.show('home', { outside: step === 'T02', carId }); }, [stage.value, step === 'T02', manifestLoaded.value, carId]);
  // Свежие числа шапки при каждом входе; до ответа — кэш (ux/03 §1.5).
  useEffect(() => {
    if (step === 'T02') return;
    app.call('getWallet').then(setWallet).catch((e: unknown) => {
      if (e instanceof ApiCallError && (e.code === 'NETWORK' || e.code === 'TIMEOUT')) return; // ST-OFFLINE: полоса сверху, данные из кэша
      app.handleError(e, { resource: tr('UI-NAV-HOME') });
    });
  }, []);
  const target = pickHomeBoss(districts.value?.data ?? null, storage.getJson<LastBattle>(LAST_BATTLE_KEY)?.origin ?? null);
  // Кэш карты пуст (первый вход после обучения) — подтянуть боссов, чтобы лист открылся с верным уровнем.
  useEffect(() => {
    if (step === 'T02' || districts.value) return;
    app.call('getDistricts').then((d) => { districts.value = { data: d, at: Date.now() }; saveCache(); }).catch(() => {});
  }, []);
  useActionBar(step === 'T02' ? { hidden: true } : {
    main: {
      text: tr('UI-BTN-FIGHT'),
      ...(lowEnergy ? { icon: 'battery' as const } : { portrait: `ICO-BOSS-${target.bossId}` }),
      testId: 'home-fight',
      onClick: () => {
        app.haptic('medium');
        // 2 тапа до боя: лист босса поверх гаража (§6 п. 3); при нехватке энергии — сразу MOD-ENERGY
        if (lowEnergy) app.nav.openSheet('MOD-ENERGY', { need: 10 });
        else app.nav.openSheet('MOD-BOSS', target);
      },
    },
  });
  return (
    <>
      {step === 'T02' ? <div style={{ height: 'calc(var(--header-h) + var(--safe-top))' }} /> : <ResourceHeader avatar />}
      <div class="content transparent" style={{ position: 'relative' }} data-testid="SCR-HOME">
        {step === 'T02' ? <LockStep /> : (
          <>
            <ZoneLayer />
            <CarPin />
            <HomeCounts />
            {!tutorialActive(step) ? <HomeRail /> : null}
            {(storage.getJson<unknown[]>('consentsRequired') ?? []).length && !tutorialActive(step) ? (
              <button class="banner warn home-rules" data-testid="home-rules" onClick={() => app.nav.openService('SCR-LEGAL')}>{tr('CL-LEGAL-UPDATED')} · {tr('CL-LEGAL-LOOK')}</button>
            ) : null}
            {!player.value ? <div class="skeleton" style={{ width: 140, height: 20 }} /> : null}
          </>
        )}
      </div>
    </>
  );
}
